import React, { useState } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

export default function SafetyWaiver({ onAccept, onDecline }) {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedSafety, setAcceptedSafety] = useState(false);

  const canProceed = acceptedTerms && acceptedSafety;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white border-4 border-black rounded-3xl shadow-brutal-lg w-full max-w-2xl my-4 flex flex-col max-h-[95vh] sm:max-h-[90vh]">
        {/* Header - Compact on mobile */}
        <div className="p-4 sm:p-6 border-b-4 border-black flex-shrink-0">
          <div className="flex items-center gap-3 mb-1 sm:mb-2">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-amber-400 rounded-xl flex items-center justify-center border-2 border-black">
              <span className="text-lg sm:text-xl">⚠️</span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black text-slate-900" style={{fontFamily: 'Outfit'}}>
              Safety First
            </h2>
          </div>
          <p className="text-slate-600 text-sm sm:text-base">Please read and acknowledge before joining</p>
        </div>

        {/* Content - Native scroll for mobile */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 overscroll-contain" style={{WebkitOverflowScrolling: 'touch'}}>
          {/* Safety Guidelines */}
          <div className="mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2 sm:mb-3 flex items-center gap-2">
              <span className="text-lg sm:text-xl">🛡️</span> Safety Guidelines
            </h3>
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-3 sm:p-4 space-y-2 sm:space-y-3">
              <div className="flex items-start gap-2 sm:gap-3">
                <span className="text-base sm:text-lg">📍</span>
                <div>
                  <p className="font-semibold text-slate-900 text-sm sm:text-base">Meet in Public Places</p>
                  <p className="text-xs sm:text-sm text-slate-600">First meetings in public locations like cafes or parks.</p>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:gap-3">
                <span className="text-base sm:text-lg">👥</span>
                <div>
                  <p className="font-semibold text-slate-900 text-sm sm:text-base">Tell Someone You Trust</p>
                  <p className="text-xs sm:text-sm text-slate-600">Share your plans with a friend or family member.</p>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:gap-3">
                <span className="text-base sm:text-lg">🔒</span>
                <div>
                  <p className="font-semibold text-slate-900 text-sm sm:text-base">Protect Personal Info</p>
                  <p className="text-xs sm:text-sm text-slate-600">Don't share home address or financial details early.</p>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:gap-3">
                <span className="text-base sm:text-lg">🚗</span>
                <div>
                  <p className="font-semibold text-slate-900 text-sm sm:text-base">Own Transportation</p>
                  <p className="text-xs sm:text-sm text-slate-600">Drive yourself or use rideshare for early meetups.</p>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:gap-3">
                <span className="text-base sm:text-lg">⚡</span>
                <div>
                  <p className="font-semibold text-slate-900 text-sm sm:text-base">Trust Your Instincts</p>
                  <p className="text-xs sm:text-sm text-slate-600">If something feels off, leave immediately.</p>
                </div>
              </div>
              <div className="flex items-start gap-2 sm:gap-3">
                <span className="text-base sm:text-lg">🚨</span>
                <div>
                  <p className="font-semibold text-slate-900 text-sm sm:text-base">Report Issues</p>
                  <p className="text-xs sm:text-sm text-slate-600">Use reporting tools for harassment or scams.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Legal Disclaimer */}
          <div className="mb-4 sm:mb-6">
            <h3 className="text-base sm:text-lg font-bold text-slate-900 mb-2 sm:mb-3 flex items-center gap-2">
              <span className="text-lg sm:text-xl">📜</span> Liability Waiver
            </h3>
            <div className="bg-slate-50 border-2 border-slate-200 rounded-2xl p-3 sm:p-4">
              <p className="text-xs sm:text-sm text-slate-700">
                By using LikeKind, I acknowledge:
              </p>
              <ul className="text-xs sm:text-sm text-slate-700 mt-2 sm:mt-3 space-y-1 sm:space-y-2 list-disc list-inside">
                <li>No background checks are conducted on users</li>
                <li>I'm responsible for my own safety</li>
                <li>LikeKind is not liable for user interactions</li>
                <li>I will follow community guidelines</li>
                <li>I am at least 18 years old</li>
              </ul>
              <p className="text-xs sm:text-sm text-slate-600 mt-3 italic">
                See full <a href="/terms" className="text-indigo-600 underline font-medium">Terms of Service</a>
              </p>
            </div>
          </div>

          {/* Checkboxes */}
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-start gap-3 p-3 bg-white border-2 border-slate-200 rounded-xl hover:border-indigo-300 transition-colors">
              <Checkbox
                id="safety-checkbox"
                checked={acceptedSafety}
                onCheckedChange={setAcceptedSafety}
                className="mt-0.5 border-2 border-black data-[state=checked]:bg-emerald-500 flex-shrink-0"
              />
              <label htmlFor="safety-checkbox" className="text-xs sm:text-sm font-medium text-slate-800 cursor-pointer">
                I have read and understand the safety guidelines. I commit to prioritizing my safety.
              </label>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white border-2 border-slate-200 rounded-xl hover:border-indigo-300 transition-colors">
              <Checkbox
                id="terms-checkbox"
                checked={acceptedTerms}
                onCheckedChange={setAcceptedTerms}
                className="mt-0.5 border-2 border-black data-[state=checked]:bg-indigo-500 flex-shrink-0"
              />
              <label htmlFor="terms-checkbox" className="text-xs sm:text-sm font-medium text-slate-800 cursor-pointer">
                I acknowledge the liability waiver and agree to the <a href="/terms" className="text-indigo-600 underline">Terms of Service</a>. I am 18+.
              </label>
            </div>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className="p-4 sm:p-6 border-t-4 border-black bg-slate-50 flex gap-3 flex-shrink-0">
          <Button
            onClick={onDecline}
            variant="outline"
            className="flex-1 h-11 sm:h-12 border-2 border-black rounded-full font-bold hover:bg-slate-100 text-sm sm:text-base"
          >
            Cancel
          </Button>
          <Button
            onClick={onAccept}
            disabled={!canProceed}
            className={`flex-1 h-11 sm:h-12 border-2 border-black rounded-full font-bold shadow-brutal transition-all text-sm sm:text-base ${
              canProceed 
                ? 'bg-indigo-500 hover:bg-indigo-600 text-white' 
                : 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
            }`}
          >
            {canProceed ? "I Agree" : "Accept Both"}
          </Button>
        </div>
      </div>
    </div>
  );
}
